import empty from "is-empty";
import { WS, illegalMessageByType } from "./types";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { LOAN_TYPES, TX_STATES } from "src/redux/Tx/constants";
import { Tx } from "src/redux/Tx/types";
import { TxActions } from "src/redux/Tx/actions";
import { ModalActions } from "src/redux/Modal/actions";
import { ToasterActions } from "src/redux/Toaster/actions";
import { Confirmations } from "src/redux/Confirmations/types";
import { Inventory } from "src/redux/Inventory/types";
import { groupBy } from "src/utils";

import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { SpinnerActions } from "src/redux/Spinner/actions";
import { SpinnerTypes } from "src/redux/Spinner/types";
import { ConfirmationActions } from "src/redux/Confirmations/actions";
import { SpinnerSelectors } from "src/redux/Spinner/selectors";
import { Availability } from "src/redux/Availability/types";
import { AvailabilitySelectors } from "src/redux/Availability/selectors";
import { ROLE } from "src/redux/TopBar/constants";
import { SettingsActions } from "src/redux/Settings/actions";
import { TxSelectors } from "src/redux/Tx/selectors";
import { RecallActions } from "src/redux/Recall/actions";
import { ClientPoolActions } from "../ClientPool/actions";

const createRecallEventHandler =
    (action) => (event) => (dispatch) => {
        event.txEntries.forEach((entry) => dispatch(action(entry)));
        dispatch(RecallActions.SetRecallListHasUpdate(true));
    };

export const WebSocketActions = {
    SetupWebSocket: (scope) => {
        return (dispatch, getState) => {
            return dispatch({
                type: `${WS.CONNECT}.${scope}`,
                url: AuthSelectors.NotificationsWssUrl(getState(), scope),
                scope,
            });
        };
    },
    PreloanSent: (event) => {
        return (dispatch) => {
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                state: TX_STATES.LOAN_REQUESTED,
                txEntries: event.txEntries.map((e) => ({
                    correlationId: event.correlationId,
                    domainId: e.id,
                    txEntry: e,
                    state: TX_STATES.LOAN_REQUESTED,
                })),
            });
        };
    },
    OfferSent: (event) => {
        return (dispatch) => {
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                state: TX_STATES.OFFERED,
                txEntries: event.txEntries.map((e) => ({
                    correlationId: event.correlationId,
                    domainId: e.id,
                    txEntry: e,
                    state: TX_STATES.OFFERED,
                })),
            });
        };
    },
    PreLoanExpired: (event) => {
        return (dispatch, getState) => {
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                state: TX_STATES.EXPIRED,
                txEntries: TxSelectors.TxEntries(getState())
                    .filter((e) => e.id === event.domainId)
                    .map((e) => ({ ...e, domainId: event.domainId })),
            });
        };
    },
    OfferExpired: (event) => {
        return (dispatch, getState) => {
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                state: TX_STATES.EXPIRED,
                txEntries: TxSelectors.TxEntries(getState())
                    .filter((e) => e.id === event.domainId)
                    .map((e) => ({ ...e, domainId: event.domainId })),
            });
        };
    },
    RejectSent: (event) => {
        return (dispatch) => {
            let rejectEntries = event.payload.ids.map((ee) => ({
                correlationId: event.correlationId,
                domainId: ee.id,
                state: TX_STATES.REJECTED,
                lastUser: event.user,
            }));
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                state: TX_STATES.REJECTED,
                txEntries: rejectEntries,
            });
        };
    },
    RejectReceived: (event) => {
        return (dispatch) => {
            const cpty = event.payload.cpty;
            let rejectEntries = event.payload.ids.map((ee) => ({
                correlationId: event.correlationId,
                domainId: ee.id,
                state: TX_STATES.REJECTED,
                comment: event.payload.comment,
            }));
            dispatch(
                ToasterActions.NewReject(
                    cpty.name,
                    event.payload.txCount ?? rejectEntries.length
                )
            );
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                state: TX_STATES.REJECTED,
                txEntries: rejectEntries,
            });
        };
    },

    RejectBatchSent: (event) => {
        return (dispatch) => {
            event.txEntries.forEach((entry) => {
                let rejectEntries = entry.payload.ids.map((ee) => ({
                    correlationId: entry.correlationId,
                    domainId: ee.id,
                    comment: entry.comment,
                    state: TX_STATES.REJECTED,
                }));
                dispatch({
                    type: Tx.UPDATE_TX_STATE_LIST,
                    state: TX_STATES.REJECTED,
                    txEntries: rejectEntries,
                });
            });
        };
    },

    TxBatchConfirmed: (event) => {
        return (dispatch, getState) => {
            const role = TopBarSelectors.Role(getState());

            if (role === ROLE.LENDER) {
                    dispatch(
                        ToasterActions.NewConfirmation(
                            event.txEntries[0].borrower.name,
                            // Only count constituent trades if it's a portfolio trade
                            event.txEntries.filter(
                                (tx) =>
                                    !tx.portfolioTrade ||
                                    (tx.parentId && !tx.removedConstituent)
                            ).length
                        )
                    );

                const groupedByBorrower = groupBy(
                    event.txEntries
                        .filter((e) => e.myRole === "LENDER")
                        .map((e) => ({
                            ...e,
                            borrower: e.borrower,
                        })),
                    "borrower"
                );
                if (!empty(groupedByBorrower)) {
                    // TODO: this should go under splits if more than 1
                    dispatch(
                        TxActions.UpsertLendTxList({
                            state: TX_STATES.CONFIRMED,
                            txEntries: event.txEntries.map((e) => ({
                                ...e,
                                correlationId: event.correlationId,
                                domainId: e.id,
                                state: TX_STATES.CONFIRMED,
                            })),
                        })
                    );
                }
                return dispatch(
                    ConfirmationActions.SetReadyToRefresh(
                        event.txEntries.map((e) => e.id)
                    )
                );
            }
        };
    },

    TxUnConfirmed: (event) => {
        return (dispatch) => {
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                state: TX_STATES.CANCELLED,
                txEntries: event.txEntries.map((e) => ({
                    ...e,
                    txEntry: e.payload.txEntry,
                    state: TX_STATES.CANCELLED,
                })),
            });
        };
    },

    TxCreated: (event) => {
        return (dispatch) => {
            dispatch(
                TxActions.UpsertBorrowList({
                    state: TX_STATES.PRE_LOAN_REQUESTED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        state: TX_STATES.PRE_LOAN_REQUESTED,
                    })),
                })
            );
        };
    },
    OfferCreated: (event) => {
        return (dispatch) => {
            // TODO: this should go under splits if more than 1
            dispatch(
                TxActions.UpdateLendOffers({
                    state: TX_STATES.OFFERED,
                    correlationId: event.correlationId,
                    txEntries: event.txEntries.map((e) => {
                        const txEntry = {
                            ...e,
                            state: TX_STATES.OFFERED,
                        };
                        return txEntry;
                    }),
                })
            );
        };
    },

    RecallActionCompleted: (event) => (dispatch) =>
        dispatch(RecallActions.SetRecallListHasUpdate(true)),

    RecallReceived: (event) => (dispatch, getState) => {
            event.txEntries.forEach((entry) =>
                dispatch(ToasterActions.NewRecall(entry))
            );
        dispatch(RecallActions.SetRecallListHasUpdate(true));
    },

    RecallDisputedReceived: (event) => (dispatch, getState) => {
            dispatch(
                ToasterActions.RecallDisputed(
                    event.txEntries[0].borrower.name,
                    event.txEntries.length
                )
            );
        dispatch(RecallActions.SetRecallListHasUpdate(true));
    },

    RecallCanceledReceived: (event) => (dispatch, getState) => {
            dispatch(
                ToasterActions.RecallCanceled(
                    event.txEntries[0].lender.name,
                    event.txEntries.length
                )
            );
        dispatch(RecallActions.SetRecallListHasUpdate(true));
    },

    SetLoanRequestedState: (event) => {
        return (dispatch) => {
            // TODO: this should go under splits if more than 1
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                txEntries: event.txEntries.map((e) => ({
                    correlationId: event.correlationId,
                    domainId: e.id,
                    txEntry: e,
                    state: TX_STATES.LOAN_REQUESTED,
                })),
            });
        };
    },
    PreLoanReceived: (event) => {
        return (dispatch, getState) => {
            const groupedByBorrower = groupBy(event.txEntries, "borrower");
                Object.keys(groupedByBorrower).forEach((e) =>
                    dispatch(
                        ToasterActions.NewLoanRequest(
                            groupedByBorrower[e][0].borrower.name,
                            // Only count constituent trades if it's a portfolio trade
                            groupedByBorrower[e].filter(
                                (tx) =>
                                    !tx.portfolioTrade ||
                                    (tx.parentId && !tx.removedConstituent)
                            ).length
                        )
                    )
                );
            dispatch({
                type: Tx.INSERT_LEND_TX_LIST,
                txEntries: event.txEntries.map((e) => {
                    let isPartner = CounterPartysSelectors.IsPartnerCpty(
                        getState(),
                        e.borrower.id
                    );

                    let reqCallable = e.requestedEconomics?.callable;
                    if (empty(reqCallable)) reqCallable = false;
                    let reqDividend = e.requestedEconomics?.dividend;

                    return {
                        ...e,
                        // set defaults
                        offeredEconomics: e.offeredEconomics || {
                            dividend: isPartner
                                ? reqDividend
                                : reqDividend ?? 100,
                            callable: reqCallable,
                        },
                        state: TX_STATES.PRE_LOAN_REQUESTED,
                    };
                }),
            });
        };
    },
    PreLoanUpdated: (event) => (dispatch) =>
        dispatch(
            TxActions.UpsertBorrowList({
                txEntries: event.txEntries,
            })
        ),
    UnsolicitedOfferMatched: (event) => (dispatch) =>
        dispatch(
            TxActions.UpdateUnsolicitedOffers({
                txEntries: event.txEntries,
            })
        ),
    PreLoanUpdateReceived: (event) => (dispatch) =>
        dispatch(
            TxActions.UpsertLendTxList({
                txEntries: event.txEntries,
            })
        ),
    OfferReceived: (event) => {
        return (dispatch, getState) => {
            const groupedByBorrower = groupBy(event.txEntries, "lender");
                Object.keys(groupedByBorrower).forEach((e) =>
                    dispatch(
                        ToasterActions.NewOffer(
                            groupedByBorrower[e][0].lender.name,
                            // Only count constituent trades if it's a portfolio trade
                            groupedByBorrower[e].filter(
                                (tx) =>
                                    !tx.portfolioTrade ||
                                    (tx.parentId && !tx.removedConstituent)
                            ).length
                        )
                    )
                );

            // TODO: this should go under splits if more than 1
            dispatch(
                TxActions.UpsertBorrowList({
                    state: TX_STATES.OFFERED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        correlationId: event.correlationId,
                        domainId: e.id,
                        state: TX_STATES.OFFERED,
                    })),
                })
            );
        };
    },
    LoanRequestReceived: (event) => {
        return (dispatch, getState) => {
            if (event.txEntries) {
                const groupedByBorrower = groupBy(event.txEntries, "borrower");
                    Object.keys(groupedByBorrower).forEach((e) =>
                        dispatch(
                            ToasterActions.NewAccept(
                                groupedByBorrower[e][0].borrower.name,
                                // Only count constituent trades if it's a portfolio trade
                                groupedByBorrower[e].filter(
                                    (tx) =>
                                        !tx.portfolioTrade ||
                                        (tx.parentId && !tx.removedConstituent)
                                ).length
                            )
                        )
                    );

                // TODO: this should go under splits if more than 1
                return dispatch({
                    type: Tx.UPDATE_TX_STATE_LIST,
                    txEntries: event.txEntries.map((e) => ({
                        correlationId: event.correlationId,
                        domainId: e.id,
                        txEntry: e,
                        state: TX_STATES.LOAN_REQUESTED,
                    })),
                });
            }
        };
    },
    NeedMatched: (event) => {
        return (dispatch, getState) => {
                dispatch(ToasterActions.NewAutoMatch(event.txEntries.length));

            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                txEntries: event.txEntries.map((e) => ({
                    ...e,
                    txEntry: e.payload.txEntry,
                    state: TX_STATES.LOAN_REQUESTED,
                })),
            });
        };
    },
    ConfirmationCanceled: (event) => {
        const ids = event.txEntries.reduce(
            (ids, e) =>
                ids.concat(
                    e.payload.txEntries
                        ? e.payload.txEntries.map((ee) => ee.id)
                        : []
                ),
            []
        );
        const lender = event.txEntries[0]?.payload?.txEntries
            ? event.txEntries[0].payload.txEntries[0].lender
            : null;
        return (dispatch, getState) => {

            const spinnerOption = SpinnerSelectors.Options(getState());
            dispatch(
                SpinnerActions.Close({
                    spinnerType: SpinnerTypes.CANCEL_CONFIRMATION,
                })
            );
            // Don't update list but notify if current user is changing value (the value will be updated locally)
            if (lender && lender.name)
                dispatch(
                    ToasterActions.ConfirmationCanceled(lender.name, ids.length)
                );
            if (
                spinnerOption.spinnerType !==
                    SpinnerTypes.CANCEL_CONFIRMATION &&
                ids.length > 0
            )
                return dispatch(ConfirmationActions.SetReadyToRefresh(ids));
        };
    },
    ConfirmationBatchCanceled: (event) => {
        return (dispatch, getState) => {
                return dispatch(
                    ToasterActions.ConfirmationCanceled(
                        event.txEntries[0].lender.name,
                        event.txEntries.length
                    )
                );
        };
    },
    ConfirmationModified: (event) => {
        const ids = event.txEntries.reduce(
            (ids, e) =>
                ids.concat(
                    e.payload.txEntries
                        ? e.payload.txEntries.map((ee) => ee.id)
                        : []
                ),
            []
        );
        const lender = event.txEntries[0]?.payload?.txEntries
            ? event.txEntries[0].payload.txEntries[0].lender
            : null;
        return (dispatch, getState) => {
            const spinnerOption = SpinnerSelectors.Options(getState());
            dispatch(
                SpinnerActions.Close({
                    spinnerType: SpinnerTypes.MODIFY_CONFIRMATION,
                })
            );
            // Don't update list but notify if current user is changing value (the value will be updated locally)
            if (lender && lender.name)
                dispatch(
                    ToasterActions.ConfirmationModified(lender.name, ids.length)
                );
            if (
                spinnerOption.spinnerType !==
                    SpinnerTypes.MODIFY_CONFIRMATION &&
                ids.length > 0
            )
                dispatch(ConfirmationActions.SetReadyToRefresh(ids));
        };
    },
    ConfirmationBatchModified: (event) => {
        return (dispatch, getState) => {
                return dispatch(
                    ToasterActions.ConfirmationModified(
                        event.txEntries[0].lender.name,
                        event.txEntries.length
                    )
                );
        };
    },
    ConfirmationReceived: (event) => {
        return (dispatch, getState) => {
            const role = TopBarSelectors.Role(getState());
            if (role === ROLE.BORROWER) {
                    dispatch(
                        ToasterActions.NewConfirmation(
                            event.txEntries[0].lender.name,
                            // Only count constituent trades if it's a portfolio trade
                            event.txEntries.filter(
                                (tx) =>
                                    !tx.portfolioTrade ||
                                    (tx.parentId && !tx.removedConstituent)
                            ).length
                        )
                    );
                // And update txs here
                const groupedByLender = groupBy(
                    event.txEntries
                        .filter((e) => e.myRole === "BORROWER")
                        .map((e) => ({
                            ...e,
                            lender: e.lender,
                        })),
                    "lender"
                );
                    Object.keys(groupedByLender).forEach((e) =>
                        dispatch(
                            ToasterActions.NewAccept(
                                groupedByLender[e][0].borrower.name,
                                // Only count constituent trades if it's a portfolio trade
                                groupedByLender[e].filter(
                                    (tx) =>
                                        !tx.portfolioTrade ||
                                        (tx.parentId && !tx.removedConstituent)
                                ).length
                            )
                        )
                    );
                if (!empty(groupedByLender)) {
                    // TODO: this should go under splits if more than 1
                    dispatch(
                        TxActions.UpsertBorrowList({
                            state: TX_STATES.CONFIRMED,
                            txEntries: event.txEntries.map((e) => ({
                                ...e,
                                correlationId: event.correlationId,
                                domainId: e.id,
                                state: TX_STATES.CONFIRMED,
                            })),
                        })
                    );
                }
            }
        };
    },
    ConfirmationBatchProjectedUpdate: (event) => {
        let entries = event.txEntries.reduce(
            (ids, e) =>
                ids.concat(
                    e.payload.txEntries
                        ? e.payload.txEntries.map((ee) => ee.id)
                        : []
                ),
            []
        );
        return ConfirmationActions.SetReadyToRefresh(entries);
    },

    ConfirmationBatchProjectedInsert: (event) => {
        let entries = event.txEntries.reduce(
            (ids, e) =>
                ids.concat(
                    e.payload.txEntries
                        ? e.payload.txEntries.map((ee) => ee.id)
                        : []
                ),
            []
        );
        return ConfirmationActions.SetReadyToRefresh(entries);
    },

    FilesStored: (event) => {
        return (dispatch) => {
            event.txEntries.forEach((e) =>
                dispatch({
                    ...e,
                    type: Confirmations.FILES_STORED,
                })
            );
        };
    },
    //Inventory ones
    InventoryBatchAdded: (event) => {
        // INVENTORY_BATCH_DELETED always precedes this event
        return (dispatch) => {
            dispatch(
                SpinnerActions.Close({ spinnerType: SpinnerTypes.INVENTORY })
            );
            return dispatch({
                type: Inventory.NEW_INVENTORY_LIST,
                inventory: event.payload.entries,
            });
        };
    },
    InventoryBatchUpdated: (event) => {
        return (dispatch) => {
            dispatch(
                SpinnerActions.Close({ spinnerType: SpinnerTypes.INVENTORY })
            );
            return dispatch({
                type: Inventory.UPDATE_INVENTORY_LIST,
                inventory: event.payload.entries,
            });
        };
    },
    InventoryBatchDeleted: (event) => {
        return (dispatch) => {
            dispatch(
                SpinnerActions.Close({ spinnerType: SpinnerTypes.INVENTORY })
            );
            return dispatch({
                type: Inventory.REMOVE_INVENTORY_LIST,
                inventory: event.payload.entries,
            });
        };
    },
    ForkCreated: (event) => {
        return (dispatch, getState) => {
            return dispatch(
                TxActions.UpsertLendTxList({
                    state: TX_STATES.FORKED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        correlationId: event.correlationId,
                        domainId: e.id,
                        state: TX_STATES.FORKED,
                    })),
                })
            );
        };
    },
    ForkReceived: (event) => {
        return (dispatch, getState) => {
            const groupedByLender = groupBy(event.txEntries, "lender");
                Object.keys(groupedByLender).forEach((e) =>
                    dispatch(
                        ToasterActions.NewFork(
                            groupedByLender[e][0].lender.name,
                            groupedByLender[e].length
                        )
                    )
                );
            return dispatch(
                TxActions.UpsertBorrowList({
                    state: TX_STATES.FORKED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        correlationId: event.correlationId,
                        domainId: e.id,
                        state: TX_STATES.FORKED,
                    })),
                })
            );
        };
    },
    TxBatchUpgradeRequested: (event) => {
        return (dispatch) => {
            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                txEntries: event.txEntries.map((e) => ({
                    correlationId: event.correlationId,
                    domainId: e.id,
                    txEntry: e,
                    state: TX_STATES.UPGRADE_REQUESTED,
                })),
            });
        };
    },
    TxBatchUpgraded: (event) => {
        return (dispatch) => {
            return dispatch(
                TxActions.UpsertBorrowList({
                    state: TX_STATES.CONFIRMED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        correlationId: event.correlationId,
                        domainId: e.id,
                        state: TX_STATES.CONFIRMED,
                    })),
                })
            );
        };
    },
    TxUpgradeBatchReceived: (event) => {
        return (dispatch, getState) => {
            const groupedByBorrower = groupBy(event.txEntries, "borrower");
            Object.keys(groupedByBorrower).forEach((e) =>
                dispatch(
                    ToasterActions.NewUpgrade(
                        groupedByBorrower[e][0].borrower.name,
                        groupedByBorrower[e].length
                    )
                )
            );
            return dispatch(
                TxActions.UpsertLendTxList({
                    state: TX_STATES.CONFIRMED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        correlationId: event.correlationId,
                        domainId: e.id,
                        state: TX_STATES.CONFIRMED,
                    })),
                })
            );
        };
    },
    TxBatchReleased: (event) => {
        return (dispatch) => {
            return dispatch(
                TxActions.UpsertBorrowList({
                    state: TX_STATES.RELEASED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        correlationId: event.correlationId,
                        domainId: e.id,
                        state: TX_STATES.RELEASED,
                    })),
                })
            );
        };
    },
    TxReleaseBatchReceived: (event) => {
        return (dispatch, getState) => {
            const groupedByBorrower = groupBy(event.txEntries, "borrower");
            Object.keys(groupedByBorrower).forEach((e) =>
                dispatch(
                    ToasterActions.NewRelease(
                        groupedByBorrower[e][0].borrower.name,
                        groupedByBorrower[e].length
                    )
                )
            );
            return dispatch(
                TxActions.UpsertLendTxList({
                    state: TX_STATES.RELEASED,
                    txEntries: event.txEntries.map((e) => ({
                        ...e,
                        correlationId: event.correlationId,
                        domainId: e.id,
                        state: TX_STATES.RELEASED,
                    })),
                })
            );
        };
    },
    TxUpgradeRequestBatchReceived: (event) => {
        // There are 2 events with same type but the second one isn't a valid txEntry
        return (dispatch, getState) => {
            if (!event.txEntries) return;

            const groupedByBorrower = groupBy(event.txEntries, "borrower");

            Object.keys(groupedByBorrower).forEach((e) =>
                dispatch(
                    ToasterActions.NewUpgradeRequest(
                        groupedByBorrower[e][0].borrower.name,
                        groupedByBorrower[e].length
                    )
                )
            );

            return dispatch({
                type: Tx.UPDATE_TX_STATE_LIST,
                txEntries: event.txEntries.map((e) => ({
                    correlationId: event.correlationId,
                    domainId: e.id,
                    txEntry: e,
                    state: TX_STATES.UPGRADE_REQUESTED,
                })),
            });
        };
    },

    OfferWithdrawn: (event) => {
        return (dispatch) => {
            event.txEntries.forEach((entry) => {
                let withdrawnEntries = entry.payload.ids.map((ee) => ({
                    correlationId: entry.correlationId,
                    domainId: ee.id,
                    comment: entry.comment,
                    state: TX_STATES.WITHDRAWN,
                }));
                dispatch({
                    type: Tx.UPDATE_TX_STATE_LIST,
                    state: TX_STATES.WITHDRAWN,
                    txEntries: withdrawnEntries,
                });
            });
        };
    },

    //Availabilities
    AvailabilityBatchReceived: (event) => {
        return (dispatch, getState) => {
            //All this is about notifying if we have genuinely new availability
            const entries = event.txEntries.reduce(
                (entries, e) =>
                    entries.concat(e.payload.entries ? e.payload.entries : []),
                []
            );
            const lenders_received = [
                ...new Set(entries.map((e) => e.lender.name)),
            ];
            lenders_received.forEach((l) => {
                //filter out all the entries for this lender from existing availablility
                const old_rows = AvailabilitySelectors.GetAvailabilityList(
                    getState()
                ).filter((e) => e.Lender === l);
                //filter out all the entries for this lender in new availablility
                const new_entries = entries.filter((e) => e.lender.name === l);
                const existing_securities = [
                    ...new Set(old_rows.map((r) => r.Name)),
                ];
                const new_securities = [
                    ...new Set(new_entries.map((e) => e.security.name)),
                ];
                const uniquely_new = new_securities.filter(
                    (e) => !existing_securities.includes(e)
                );
                // console.log(
                //     "Going to compare availability contents for " +
                //         JSON.stringify(l) +
                //         ". Got existing availability of " +
                //         JSON.stringify(old_rows) +
                //         " and new availability of " +
                //         JSON.stringify(new_entries)
                // );
                // console.log(
                //     "Existing securities are " +
                //         JSON.stringify(existing_securities) +
                //         " and new_securities are " +
                //         JSON.stringify(new_securities) +
                //         " Found new securities " +
                //         JSON.stringify(uniquely_new)
                // );
                if (uniquely_new.length > 0)
                    dispatch(
                        ToasterActions.NewAvailability(l, uniquely_new.length)
                    );
            });
            //Now do the update
            return dispatch({
                type: Availability.UPDATE_AVAILABILITY_LIST,
                entries,
            });
        };
    },

    AvailabilityBatchDeleted: (event) => {
        const entries = event.txEntries.reduce(
            (entries, e) =>
                entries.concat(e.payload.entries ? e.payload.entries : []),
            []
        );
        return (dispatch) => {
            return dispatch({
                type: Availability.DELETE_AVAILABILITY_LIST,
                entries,
            });
        };
    },

    //settings
    CptySettingsUpdated: (event) => {
        return (dispatch) => {
            const { cptySettings } = event.txEntries[0].payload;
            if (cptySettings.role === "BORROWER") {
                return dispatch(
                    SettingsActions.UpdateBorrowerSettings(cptySettings)
                );
            } else {
                return dispatch(
                    SettingsActions.UpdateLenderSettings(cptySettings)
                );
            }
        };
    },

    ClientPoolUpdated: (event) => (dispatch) =>
        dispatch(ClientPoolActions.SetHaveUpdates(true)),

    TradeActivityUpdated: () => {
        return (dispatch) => {
            try {
                const { DashboardActions } = require("src/redux/Dashboard/actions");
                dispatch(DashboardActions.FetchAll());
            } catch (e) {
                console.error("Failed to refresh dashboard on trade activity update", e);
            }
        };
    },

    //Errors
    SendFailed: (event) => {
        return (dispatch, getState) => {
            event.txEntries.forEach((e) => {
                    dispatch(
                        ToasterActions.NewError(
                            "Error Sending Tx: " + e.domainId
                        )
                    );
                return dispatch({
                    type: Tx.UPDATE_TX_STATE.payload.txEntry,
                    state: TX_STATES.ERROR,
                    domainId: e.domainId,
                    correlationId: e.correlationId,
                });
            });
        };
    },
    ErrorOfferExpired: (event) => {
        return (dispatch) => {
            dispatch(
                ToasterActions.NewError(
                    "Offer already expired: " + event.domainId
                )
            );
            return dispatch({
                type: Tx.UPDATE_TX_STATE,
                state: TX_STATES.ERROR,
                domainId: event.domainId,
                correlationId: event.correlationId,
            });
        };
    },
    ErrorTxIllegalStatus: (event) => {
        return (dispatch) => {
            const message = illegalMessageByType(event.type);
            event.txEntries.forEach((e) => {
                dispatch(
                    ToasterActions.NewError(
                        message || "Illegal Tx state: " + e.payload.payload
                    )
                );
            });
            // It should be just enough to refresh only the failed entries
            // from event.txEntries.*.payload.payload.
            // Or do not change UI status before the actual backend status change
            return dispatch(TxActions.UpdateEntries());
        };
    },
    UnknownError: (event) => {
        return (dispatch) => {
            dispatch(
                ToasterActions.NewInfo(
                    "An unexpected error occurred. Please give technical support correlationId " +
                        event.correlationId
                )
            );
            return dispatch(ModalActions.Close); //Ensure modal is closed on any errors
        };
    },
};
